import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { twMerge } from 'tailwind-merge';

interface ToolCall {
  name: string;
  status: 'pending' | 'done' | 'error';
}

interface AgentExecutionCardProps {
  agentName: string;
  agentTitle: string;
  status: 'running' | 'completed' | 'error';
  toolCount: number;
  tokenCount: number;
  isExpanded: boolean;
  onToggle: () => void;
  toolCalls?: ToolCall[];
}

export const AgentExecutionCard = ({
  agentName,
  agentTitle: _agentTitle,
  status,
  toolCount,
  tokenCount,
  isExpanded,
  onToggle,
  toolCalls = [],
}: AgentExecutionCardProps) => {
  const formatTokens = (count: number) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  const getStatusText = () => {
    switch (status) {
      case 'running':
        return 'Analyzing...';
      case 'completed':
        return 'Done';
      case 'error':
        return 'Error';
    }
  };

  return (
    <div className="border-chariot-border bg-chariot-layer1 rounded-lg border p-4">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <span
            className={twMerge(
              'font-mono text-sm font-semibold',
              status === 'completed' && 'text-success',
              status === 'running' && 'text-blue-500',
              status === 'error' && 'text-red-500'
            )}
          >
            {agentName}
          </span>

          {status === 'running' && (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          )}

          <span className="text-chariot-text-secondary text-sm">
            {getStatusText()}
          </span>

          {status === 'completed' && (
            <div className="text-chariot-text-secondary flex items-center gap-3 text-xs">
              <span>{toolCount} tools</span>
              <span>{formatTokens(tokenCount)} tokens</span>
            </div>
          )}
        </div>

        {isExpanded ? (
          <ChevronUpIcon className="text-chariot-text-secondary h-4 w-4" />
        ) : (
          <ChevronDownIcon className="text-chariot-text-secondary h-4 w-4" />
        )}
      </button>

      {/* Expandable tool list using CSS Grid animation */}
      <div
        className={twMerge(
          'grid transition-[grid-template-rows,opacity] duration-300 ease-in-out',
          isExpanded
            ? 'grid-rows-[1fr] opacity-100'
            : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          {toolCalls.length > 0 && (
            <div className="mt-4 space-y-2">
              {toolCalls.map((tool, idx) => (
                <div
                  key={idx}
                  className="text-chariot-text-secondary flex items-center gap-2 text-sm"
                >
                  {tool.status === 'done' && (
                    <span className="text-success">✓</span>
                  )}
                  {tool.status === 'error' && (
                    <span className="text-red-500">✕</span>
                  )}
                  {tool.status === 'pending' && (
                    <span className="text-yellow-500">◐</span>
                  )}
                  <span>{tool.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
